import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Loader2, RefreshCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useCredits } from "@/hooks/useCredits";
import {
  TEMPLATE_STEP1_CREDITS,
  calculateStep2Credits,
  type TemplateDuration,
  type TemplateResolution,
} from "@/lib/templatePricing";
import { track } from "@/lib/analytics";
import { formatCategorySummary } from "@/lib/transformationCategories";
import type { CategoryParams } from "@/lib/buildTransformationPrompt";

export const Route = createFileRoute("/_authenticated/create/template/$id/preview")({
  head: () => ({
    meta: [
      { title: "生成中 | 変身テンプレ | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PreviewPage,
});

type Row = {
  user_id: string;
  template_state: string | null;
  intermediate_image_url: string | null;
  video_resolution: string | null;
  video_duration_requested: number | null;
  video_with_audio: boolean | null;
  regenerate_count: number | null;
  error_message: string | null;
  category_id: string | null;
  category_params: CategoryParams | null;
};

function PreviewPage() {
  const { id } = useParams({ from: "/_authenticated/create/template/$id/preview" });
  const navigate = useNavigate();
  const [row, setRow] = useState<Row | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const balance = useCredits(userId);
  const step1CompleteFiredRef = useRef(false);
  const step2StartedRef = useRef(false);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from("generations")
        .select(
          "user_id, template_state, intermediate_image_url, video_resolution, video_duration_requested, video_with_audio, regenerate_count, error_message, category_id, category_params",
        )
        .eq("id", id)
        .maybeSingle();
      if (!active) return;
      if (error || !data) return;
      setRow(data as Row);
    };
    void fetchOnce();
    const t = window.setInterval(fetchOnce, 3000);
    return () => {
      active = false;
      window.clearInterval(t);
    };
  }, [id]);

  const state = row?.template_state ?? "";
  const isProcessing = state === "step1_processing" || state === "";
  const isFailed = state === "step1_failed";
  const isReady = state === "step1_complete" && !!row?.intermediate_image_url;

  const resolution = (row?.video_resolution ?? "1080p") as TemplateResolution;
  const duration = (row?.video_duration_requested ?? 5) as TemplateDuration;
  const withAudio = !!row?.video_with_audio;
  const step2Credits = calculateStep2Credits(resolution, duration, withAudio);

  // step1 完了 → 自動で step2 を開始
  useEffect(() => {
    if (!isReady || step2StartedRef.current) return;
    step2StartedRef.current = true;
    if (!step1CompleteFiredRef.current) {
      step1CompleteFiredRef.current = true;
      track("template_step1_complete", {
        generation_id: id,
        regenerate_count: row?.regenerate_count ?? 0,
      });
    }
    (async () => {
      if (balance !== null && balance < step2Credits) {
        toast.error(`動画化には ${step2Credits}cr 必要です（残高 ${balance}cr）`);
        return;
      }
      try {
        track("template_step2_start", {
          generation_id: id,
          resolution,
          duration,
          audio: withAudio,
          credits: step2Credits,
        });
        const { error } = await supabase.functions.invoke("create-template-step2", {
          body: { generation_id: id },
        });
        if (error) throw error;
        navigate({ to: "/create/template/$id/processing", params: { id } });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "動画生成の開始に失敗しました");
        step2StartedRef.current = false;
      }
    })();
  }, [isReady, balance, step2Credits, id, resolution, duration, withAudio, navigate, row?.regenerate_count]);

  const handleRetry = async () => {
    if (retrying || !row) return;
    if (balance !== null && balance < TEMPLATE_STEP1_CREDITS) {
      toast.error(`再試行には ${TEMPLATE_STEP1_CREDITS}cr 必要です`);
      return;
    }
    setRetrying(true);
    try {
      track("template_step1_regenerate", {
        generation_id: id,
        regenerate_count: (row.regenerate_count ?? 0) + 1,
      });
      const { data: gen } = await supabase
        .from("generations")
        .select(
          "input_video_url, selfie_frame_url, background_url, category_id, category_params",
        )
        .eq("id", id)
        .maybeSingle();
      if (!gen) throw new Error("素材を取得できません");
      if (!gen.category_id || !gen.selfie_frame_url) {
        toast.error("旧バージョンのデータは再生成に対応していません");
        setRetrying(false);
        return;
      }
      const { error } = await supabase.functions.invoke("create-template-step1", {
        body: {
          existing_generation_id: id,
          selfie_video_url: gen.input_video_url,
          selfie_frame_url: gen.selfie_frame_url,
          background_url: gen.background_url,
          category_id: gen.category_id,
          category_params: gen.category_params ?? {},
          video_resolution: resolution,
          video_duration: duration,
          video_with_audio: withAudio,
          aspect_ratio: "9:16",
        },
      });
      if (error) {
        let body: any = null;
        try { body = await (error as any).context?.json?.(); } catch { /* ignore */ }
        if (body?.error === "CONTENT_BLOCKED") {
          toast.error("不適切なコンテンツが検出されたため生成できません。別の素材でお試しください。");
          return;
        }
        if (body?.error === "MODERATION_UNAVAILABLE") {
          toast.error("コンテンツ確認サービスが一時停止中です。時間を置いてお試しください。");
          return;
        }
        throw new Error(body?.error ? `${body.error}: ${body.detail ?? ""}` : (error as Error).message);
      }
      step2StartedRef.current = false;
      toast.success("再試行しました");

    } catch (e) {
      toast.error(e instanceof Error ? e.message : "再試行に失敗しました");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            動画を生成中
          </div>
          <h1 className="text-2xl font-bold sm:text-3xl">そのままお待ちください</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            完了すると自動で結果画面に移動します。
          </p>
        </header>

        <Card className="overflow-hidden">
          <div className="aspect-[9/16] w-full bg-muted">
            {(isProcessing || isReady) && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm">生成中...（1〜3分）</p>
              </div>
            )}
            {isFailed && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-sm font-semibold text-destructive">生成に失敗しました</p>
                <p className="text-xs text-muted-foreground">
                  {row?.error_message ?? "もう一度お試しください。"}
                </p>
                <Button onClick={handleRetry} disabled={retrying}>
                  {retrying ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="mr-1 h-4 w-4" />
                  )}
                  もう一度試す
                </Button>
              </div>
            )}
          </div>
        </Card>

        {row?.category_id && (
          <div className="mt-4 rounded-lg border border-border bg-card/50 p-3">
            <div className="mb-1 text-xs text-muted-foreground">変身設定</div>
            <div className="text-sm font-medium">
              {formatCategorySummary(row.category_id, row.category_params as Record<string, unknown> | null)}
            </div>
          </div>
        )}

        <p className="mt-3 text-center text-xs text-muted-foreground">
          残高: <span className="font-bold text-foreground">{balance ?? "—"}cr</span>
        </p>
      </div>
    </main>
  );
}
